import { PlanTier } from '@flagsync/skyline';
import { zodResolver } from '@hookform/resolvers/zod';
import { UseFormReturn, useForm } from 'react-hook-form';

import { useToast } from '@/components/ui/use-toast';

import { useRegister } from '@/pages/unauthenticated/auth/mutations/use-register';
import { DEFAULT_FORM_VALUES } from '@/pages/unauthenticated/auth/login/form/form-schema';
import {
  RegisterFormValues,
  registerFormSchema,
} from '@/pages/unauthenticated/auth/register/form/form-schema';
import { usePasswordStrength } from '@/hooks/use-password-strength';
import { JobTitleCode } from '@/services/types';
import { ServiceErrorCode } from '@/services/utils/service-error';

export interface UseRegisterForm {
  form: UseFormReturn<RegisterFormValues>;
  onSubmit: (values: RegisterFormValues) => Promise<void>;
  isLoading: boolean;
  isSuccess: boolean;
  passwordStrength: number;
}

export const useRegisterForm = (): UseRegisterForm => {
  const { toast } = useToast();

  const form = useForm<RegisterFormValues>({
    resolver: zodResolver(registerFormSchema),
    defaultValues: {
      ...DEFAULT_FORM_VALUES,
      jobTitle: JobTitleCode.SOFTWARE_ENGINEER,
      planTier: PlanTier.Tier320,
    },
  });

  const passwordStrength = usePasswordStrength(
    form.watch('password'),
    form.getValues('password'),
  );

  const { isPending: isLoading, mutate, isSuccess } = useRegister({
    onSuccess: () => {
      form.clearErrors();
    },
    onError: (error) => {
      const { errorCode, validationErrorMap } = error;

      if (errorCode === ServiceErrorCode.DuplicateEmail) {
        form.setError('email', {
          message: 'That email is already in use, did you mean to login?',
        });
        return;
      }

      if (errorCode === ServiceErrorCode.ValidationGuard) {
        if (validationErrorMap.password) {
          form.setError('password', {
            message: "Hmm, this password doesn't seem strong enough",
          });
        }
        return;
      }

      if (errorCode === ServiceErrorCode.CloudflareTurnstileHttpFailure) {
        form.setError('cfTurnstileResponse', {
          message:
            'Verification could not be completed due a Cloudflare error. Please try again shortly.',
        });
        return;
      }

      if (errorCode === ServiceErrorCode.CloudflareTurnstile) {
        form.setError('cfTurnstileResponse', {
          message:
            'Sorry, Cloudflare doesn’t think that you’re human. Please refresh the page and try again.',
        });
        return;
      }

      if (errorCode === ServiceErrorCode.CloudflareTurnstileMissing) {
        form.setError('cfTurnstileResponse', {
          message:
            'Cloudflare didn’t generate a verification token. Please refresh the page and try again.',
        });
        return;
      }

      const errorMessage = error.getErrorMessage({
        title: 'Uh oh! Something went wrong.',
        message: 'There was a problem registering your account.',
      });

      toast({
        variant: 'destructive',
        title: errorMessage.title,
        description: errorMessage.message,
      });
    },
  });

  const onSubmit = async (values: RegisterFormValues) => {
    mutate(values);
  };

  return { form, onSubmit, isLoading, isSuccess, passwordStrength };
};
